import React, { useEffect, useRef, useState } from "react";
import { Item, ComboBox } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useTranslation } from "react-i18next";
import { useDependency } from "../../../../../../contexts/DependencyProvider";
import { ListJournalRequest } from "../../../../../../services/soap/finance/requests/ListJournalRequest";
import { ListUserRequest } from "../../../../../../services/soap/access/requests/ListUserRequest";
interface User {
    id: string;
    name: string;
}
interface IUserDropDownProps {
    selectedUser: string;              // parent selected id
    onChange: (data: any | null) => void; // null means cleared
    isEdit: any;
    isRequired?: any;
    errorMessage?: any;
    validationState?: any;
    label?: any;
    width?: any;
}

const UserDropDown: React.FC<IUserDropDownProps> = ({
    selectedUser,
    onChange,
    isEdit,
    isRequired,
    errorMessage,
    validationState,
    label,
    width
}) => {
    const { store, accessService } = useDependency();
    const { t } = useTranslation("invoice");
    const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });
    const resolvedLabel = label ?? _t("issued_by");

    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [inputValue, setInputValue] = useState<string>("");

    const didInitialAutoSelectRef = useRef(false);
    const isUserEditingRef = useRef(false);

    const list = useAsyncList<User>({
        async load({ filterText }) {
            try {
                const request = new ListUserRequest(store.Server, store.SessionId);
                const response = await accessService.listUser(request);
                let items: any[] = response?.USERS.map(({ USER }) => {
                    return {
                        id: USER.id,
                        name: `${USER.name1} ${USER.name3}`,
                    }
                });
                const ft = (filterText ?? "").trim().toLowerCase();
                if (ft) {
                    items = items.filter((x) => (x.name ?? "").toLowerCase().includes(ft));
                }

                return { items };
            } catch (e) {
                console.error("Error fetching ItemStatus:", e);
                return { items: [] };
            }
        },
    });

    useEffect(() => {
        list.reload();
    }, []);

    // ✅ initial load: auto-select 0th item ONCE
    useEffect(() => {
        if (!didInitialAutoSelectRef.current && list.items.length > 0 && !isEdit) {
            didInitialAutoSelectRef.current = true;

            const first = list.items[0];
            const firstId = String((first as any).id);

            setSelectedKey(firstId);
            setInputValue(`${first.name ?? ""}`);
            onChange(first);
        }
    }, [list.items, isEdit, onChange]);

    // ✅ sync from parent ONLY when user is not actively typing
    useEffect(() => {
        if (isUserEditingRef.current) return;

        if (!selectedUser) {
            setSelectedKey(null);
            setInputValue("");
            return;
        }

        setSelectedKey(String(selectedUser));
        const selected = list.items.find((x) => String((x as any).id) === String(selectedUser));
        setInputValue(`${selected?.name ?? ""}`);
    }, [selectedUser, list.items]);

    const handleInputChange = (val: string) => {
        isUserEditingRef.current = true;

        setInputValue(val);
        list.setFilterText(val);

        // ✅ IMPORTANT: allow deleting text even if something is selected
        // If user input diverges from selected option label, clear the selectedKey
        if (selectedKey) {
            const selected = list.items.find((x) => String((x as any).id) === String(selectedKey));
            const selectedLabel = `${selected?.name ?? ""}`;
            if (val !== selectedLabel) {
                setSelectedKey(null);
            }
        }

        // ✅ goal #4: when cleared -> empty selection
        if (!val.trim()) {
            setSelectedKey(null);
            list.setFilterText(""); // reset filtering
            onChange(null);         // tell parent it is cleared
        }
    };

    const handleSelectionChange = (key: React.Key | null) => {
        isUserEditingRef.current = false;

        const idStr = key ? String(key) : null;
        setSelectedKey(idStr);

        if (!idStr) return;

        const selected = list.items.find((x) => String((x as any).id) === idStr);
        if (selected) {
            setInputValue(`${selected.name ?? ""}`); // show immediately
            onChange(selected);

            // clear filter so next focus shows all values
            list.setFilterText("");
        }
    };

    return (
        <ComboBox
            label={resolvedLabel}
            aria-label={resolvedLabel}
            items={list.items}
            selectedKey={selectedKey}
            inputValue={inputValue}
            loadingState={list.loadingState}
            width={width}
            isDisabled={isEdit}
            isRequired={isRequired}
            errorMessage={errorMessage}
            validationState={validationState}
            onFocus={() => {
                // ✅ goal #2 show all values when user clicks in
                isUserEditingRef.current = true;
                list.setFilterText("");
            }}
            onBlur={() => {
                isUserEditingRef.current = false;
            }}
            onInputChange={handleInputChange}
            onSelectionChange={handleSelectionChange}
        >
            {(item) => (
                <Item key={(item as any).id} textValue={`${item.name ?? ""}`}>
                    {`${item.name ?? ""}`}
                </Item>
            )}
        </ComboBox>
    );
};

export default UserDropDown;
